import React, { useEffect, useState, useMemo, useRef } from "react";
import { io } from "socket.io-client";
import OBSWebSocket from "obs-websocket-js";
import "./App.css";

//
// SOCKET.IO CONNECTION
//
const socket = io("https://api.theviewage.site", {
  path: "/socket.io",
  transports: ["websocket"],
  withCredentials: true,
});

// -----------------------------
// Viewer metrics endpoint
// -----------------------------
const METRICS_CURRENT_URL = "https://api.theviewage.site/api/metrics/current";

function timeSince(ts) {
  if (!ts) return "";

  const s = Math.floor((Date.now() - ts) / 1000);
  let value, unit;

  if (s < 60) {
    return "Now ";
  } else {
    const m = Math.floor(s / 60);
    if (m < 60) {
      value = m;
      unit = "m";
    } else {
      const h = Math.floor(m / 60);
      if (h < 24) {
        value = h;
        unit = "h";
      } else {
        const d = Math.floor(h / 24);
        value = d;
        unit = "d";
      }
    }
  }

  const v = value < 10 ? `\u00A0${value}` : `${value}`;
  return `${v}${unit} `;
}

function App() {
  const [donationsAndBits, setDonationsAndBits] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [raids, setRaids] = useState([]);

  const [obs, setObs] = useState(null);
  const [obsConnected, setObsConnected] = useState(false);
  const [obsIP, setObsIP] = useState("127.0.0.1");
  const [obsPort, setObsPort] = useState("4455");
  const [obsPassword, setObsPassword] = useState("");
  const [obsSceneItemId, setObsSceneItemId] = useState(null);
  const [obsSceneName, setObsSceneName] = useState("¬ Battery Cam"); // your scene name
  const [obsSourceName, setObsSourceName] = useState("Battery-Camera");

  // ⭐ Mass Gift Suppression Tracker
  const [massGiftTracker, setMassGiftTracker] = useState({
    gifter: null,
    remaining: 0,
  });

  // ============================================================
  // ⭐ LOAD OLD EVENTS FROM DATABASE ON PAGE LOAD
  // ============================================================
  useEffect(() => {
    async function loadOldEvents() {
      try {
        const res = await fetch(
          "https://api.theviewage.site/api/events?limit=999999",
          {
            credentials: "include",
          }
        );
        if (!res.ok) return;

        const data = await res.json();

        const converted = data.map((e) => {
          let parsedRoot = {};
          try {
            // If backend ever starts sending raw directly, use that
            parsedRoot = e.raw ? e.raw : JSON.parse(e.raw_json || "{}");
          } catch {
            parsedRoot = {};
          }

          // 🔍 Figure out where the real Twitch/Streamlabs message lives:
          // 1) Normalized event stored in DB → original is in parsedRoot.raw.message[0]
          // 2) Raw upstream event stored → original is in parsedRoot.message[0]
          let rawItem = null;

          if (
            parsedRoot &&
            parsedRoot.raw &&
            Array.isArray(parsedRoot.raw.message) &&
            parsedRoot.raw.message.length > 0 &&
            typeof parsedRoot.raw.message[0] === "object"
          ) {
            // Case 1: DB stored normalized event; original message is nested
            rawItem = parsedRoot.raw.message[0];
          } else if (
            parsedRoot &&
            Array.isArray(parsedRoot.message) &&
            parsedRoot.message.length > 0 &&
            typeof parsedRoot.message[0] === "object"
          ) {
            // Case 2: DB stored the upstream event directly
            rawItem = parsedRoot.message[0];
          } else {
            // Fallback: just use the root object
            rawItem = parsedRoot || {};
          }

          // 🧷 Raid viewers fallback:
          // if raiders is missing but we have an amount on the row, use that
          if (rawItem.raiders == null && e.amount != null) {
            rawItem.raiders = e.amount;
          }

          // ✅ Normalize structure to exactly match live events:
          // getRaw(e) → e.raw.message[0]
          const normalizedRaw = {
            message: [rawItem],
          };

          return {
            ...e,
            raw: normalizedRaw,
            createdAt: new Date(e.created_at).getTime(),
            isOld: true,
          };
        });

        const oldDonos = [];
        const oldSubs = [];
        const oldFollows = [];
        const oldRaids = [];

        converted.forEach((ev) => {
          const cat = String(ev.category || "").toLowerCase();

          if (["donation", "bits"].includes(cat)) {
            oldDonos.push(ev);
          } else if (
            ["subscription", "resub", "sub", "gifted_sub", "mass_gift"].includes(
              cat
            )
          ) {
            oldSubs.push(ev);
          } else if (cat === "follow") {
            oldFollows.push(ev);
          } else if (cat === "raid") {
            oldRaids.push(ev);
          }
        });

        const sortDesc = (arr) =>
          arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        setDonationsAndBits((prev) => [...sortDesc(oldDonos), ...prev]);
        setSubscribers((prev) => [...sortDesc(oldSubs), ...prev]);
        setFollowers((prev) => [...sortDesc(oldFollows), ...prev]);
        setRaids((prev) => [...sortDesc(oldRaids), ...prev]);
      } catch (err) {
        console.error("Failed to load old events:", err);
      }
    }

    loadOldEvents();
  }, []);
  // ============================================================
  
  function refreshSupportStats() {
    const now = Date.now();

    // Grey out everything currently on screen
    setDonationsAndBits((prev) => prev.map((e) => ({ ...e, isOld: true })));
    setSubscribers((prev) => prev.map((e) => ({ ...e, isOld: true })));
    setFollowers((prev) => prev.map((e) => ({ ...e, isOld: true })));
    setRaids((prev) => prev.map((e) => ({ ...e, isOld: true })));

    // Reset stream stats “session”
    setLiveFlags({ twitch: false, youtube: false });
    setTwitchGameName("");
    setViewerNow({ twitch: null, youtube: null });

    // Immediately fetch current state again (updates directory/viewers quickly)
    pollMetricsOnce();
  }

  const [supportOpen, setSupportOpen] = useState(
    JSON.parse(localStorage.getItem("supportOpen")) ?? true
  );
  const [camStatusOpen, setCamStatusOpen] = useState(
    JSON.parse(localStorage.getItem("camStatusOpen")) ?? false
  );
  const [camControlOpen, setCamControlOpen] = useState(
    JSON.parse(localStorage.getItem("camControlOpen")) ?? false
  );
  const [obsStatus, setObsStatus] = useState(
    JSON.parse(localStorage.getItem("obsStatusOpen")) ?? false
  );

  // -----------------------------
  // Tech panels master toggle (Cam Status + Cam Control + OBS)
  // Hidden by default
  // -----------------------------
  const [techPanelsEnabled, setTechPanelsEnabled] = useState(
    localStorage.getItem("techPanelsEnabled") === "true"
  );

  const [supportStatusOpen, setSupportStatusOpen] = useState(
    JSON.parse(localStorage.getItem("supportStatusOpen")) ?? true
  );

  // -----------------------------
  // Support Status (viewer metrics + Twitch game)
  // -----------------------------
  const [twitchGameName, setTwitchGameName] = useState("");
  const [liveFlags, setLiveFlags] = useState({ twitch: false, youtube: false });
  const [viewerSeries, setViewerSeries] = useState([]); // blank until live
  const [viewerNow, setViewerNow] = useState({ twitch: null, youtube: null }); // ✅ NEW

  const metricsPollRef = useRef(null);
  const chartCanvasRef = useRef(null);
  const chartWrapRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("supportOpen", JSON.stringify(supportOpen));
  }, [supportOpen]);

  useEffect(() => {
    localStorage.setItem("camStatusOpen", JSON.stringify(camStatusOpen));
  }, [camStatusOpen]);

  useEffect(() => {
    localStorage.setItem("camControlOpen", JSON.stringify(camControlOpen));
  }, [camControlOpen]);

  useEffect(() => {
    localStorage.setItem("obsStatusOpen", JSON.stringify(obsStatus));
  }, [obsStatus]);

  useEffect(() => {
    localStorage.setItem("techPanelsEnabled", String(techPanelsEnabled));
  }, [techPanelsEnabled]);

  useEffect(() => {
    localStorage.setItem("supportStatusOpen", JSON.stringify(supportStatusOpen));
  }, [supportStatusOpen]);

  function stopMetricsPolling() {
    if (metricsPollRef.current) {
      clearInterval(metricsPollRef.current);
      metricsPollRef.current = null;
    }
  }

  async function pollMetricsOnce() {
    try {
      const res = await fetch(METRICS_CURRENT_URL, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();

      const twitchLive = !!data.twitch_is_live;
      const youtubeLive = !!data.youtube_is_live;

      setLiveFlags({ twitch: twitchLive, youtube: youtubeLive });
      setTwitchGameName(data.twitch_game_name || "");
	  
	  setViewerNow({
        twitch: twitchLive ? (data.twitch_viewers ?? null) : null,
        youtube: youtubeLive ? (data.youtube_viewers ?? null) : null,
      });

      // Append points only when at least one platform is live.
      // Don't plot 0 when a platform is offline — store null to break the line.
      if (twitchLive || youtubeLive) {
        setViewerSeries((prev) => {
          const next = prev.slice();
          next.push({
            ts: (data.ts || Math.floor(Date.now() / 1000)) * 1000,
            twitch: twitchLive ? (data.twitch_viewers ?? null) : null,
            youtube: youtubeLive ? (data.youtube_viewers ?? null) : null,
          });
          // Prevent unbounded growth in the browser
          if (next.length > 6000) next.splice(0, next.length - 6000);
          return next;
        });
      }
    } catch (err) {
      // ignore transient errors
    }
  }

  function stopMetricsPolling() {
    if (metricsPollRef.current) {
      clearTimeout(metricsPollRef.current);
      metricsPollRef.current = null;
    }
  }

  // Single scheduler: always polls, changes rate based on liveFlags + online state
  useEffect(() => {
    let cancelled = false;

    const FAST_MS = 15000; // live
    const SLOW_MS = 60000; // offline / not live
    const ERR_MS  = 15000; // retry after transient failure

    async function tick() {
     if (cancelled) return;

      // If browser is offline, don't even try fetch; just re-check soon.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        metricsPollRef.current = setTimeout(tick, SLOW_MS);
        return;
      }

      // Poll once; if it throws, we still keep the loop alive.
     try {
        await pollMetricsOnce();
        const shouldPollFast = liveFlags.twitch || liveFlags.youtube;
        metricsPollRef.current = setTimeout(tick, shouldPollFast ? FAST_MS : SLOW_MS);
      } catch {
        metricsPollRef.current = setTimeout(tick, ERR_MS);
      }
    }

    // Kick immediately on mount
    tick();

    // Also kick immediately when connection comes back / tab becomes visible
    const onOnline = () => tick();
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVis);

    return () => {
     cancelled = true;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVis);
      stopMetricsPolling();
    };
    // IMPORTANT: depend on liveFlags so we can switch FAST/SLOW immediately
  }, [liveFlags.twitch, liveFlags.youtube]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw chart
  useEffect(() => {
    function niceStep(rawStep) {
      // Returns a "nice" step size using 1, 2, 2.5, 5, 10 * 10^n
      if (!isFinite(rawStep) || rawStep <= 0) return 1;
      const exp = Math.floor(Math.log10(rawStep));
      const f = rawStep / Math.pow(10, exp);
      let nf;
      if (f <= 1) nf = 1;
      else if (f <= 2) nf = 2;
      else if (f <= 2.5) nf = 2.5;
      else if (f <= 5) nf = 5;
      else nf = 10;
      return nf * Math.pow(10, exp);
    }

    function draw() {
      const canvas = chartCanvasRef.current;
      const wrap = chartWrapRef.current;
      if (!canvas || !wrap) return;

      const styles = getComputedStyle(wrap);
      const padLpx = parseFloat(styles.paddingLeft) || 0;
      const padRpx = parseFloat(styles.paddingRight) || 0;
      const w = Math.max(1, wrap.clientWidth - padLpx - padRpx);
      const h = 220;

      // HiDPI
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";

      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const points = viewerSeries;

      // Chart paddings (space for axis labels)
      const padL = 54;
      const padR = 14;
      const padT = 12;
      const padB = 36;

      const x0 = padL;
      const y0 = padT;
      const x1 = w - padR;
      const y1 = h - padB;

      // Determine bounds (ignore nulls so offline doesn't drag the scale)
      const vals = [];
      for (const p of points) {
        if (typeof p.twitch === "number") vals.push(p.twitch);
        if (typeof p.youtube === "number") vals.push(p.youtube);
      }
      const maxYRaw = vals.length ? Math.max(...vals) : 0;
      const maxYMin = Math.max(1, maxYRaw);

      // Build "nice" Y scale with ~5 ticks
      const targetTicks = 5; // includes 0
      const rawStep = maxYMin / (targetTicks - 1);
      const step = niceStep(rawStep);
      const yMax = Math.max(step, Math.ceil(maxYMin / step) * step);

      function yScale(v) {
        const t = v / yMax;
        return y1 - t * (y1 - y0);
      }

      // Time range for X labels
      const hasTime = points.length >= 2;
      const minTs = hasTime ? points[0].ts : Date.now() - 60 * 60 * 1000;
      const maxTs = hasTime ? points[points.length - 1].ts : Date.now();

      // --- Grid + Axes ---
      ctx.lineWidth = 1;
      ctx.font = "12px sans-serif";

      // Horizontal grid lines + Y labels
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      for (let v = 0; v <= yMax + 0.0001; v += step) {
        const y = yScale(v);
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.lineTo(x1, y);
        ctx.stroke();

        ctx.fillText(String(Math.round(v)), 10, y + 4);
      }

      // Axes lines
      ctx.strokeStyle = "rgba(255,255,255,0.25)";
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x0, y1);
      ctx.lineTo(x1, y1);
      ctx.stroke();

      // Vertical grid lines + X labels
      const xTicks = 4; // 5 labels
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      for (let i = 0; i <= xTicks; i++) {
        const t = i / xTicks;
        const x = x0 + t * (x1 - x0);

        ctx.beginPath();
        ctx.moveTo(x, y0);
        ctx.lineTo(x, y1);
        ctx.stroke();

        const ts = minTs + t * (maxTs - minTs);
        const label = new Date(ts).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const tw = ctx.measureText(label).width;
        ctx.fillText(label, x - tw / 2, h - 12);
      }

      // Lines (break on nulls)
      const n = points.length;
      const xStep = n > 1 ? (x1 - x0) / (n - 1) : 0;

      function drawLine(key, stroke) {
        if (!n) return;
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 2;
        ctx.beginPath();
        let started = false;

        for (let i = 0; i < n; i++) {
          const v = points[i][key];
          if (typeof v !== "number") {
            started = false;
            continue;
          }
          const x = x0 + i * xStep;
          const y = yScale(v);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }

      // Twitch = purple, YouTube = red
      const TWITCH = "rgba(243, 104, 255, 0.92)";
      const YT = "rgba(255, 80, 80, 0.9)";
      drawLine("twitch", TWITCH);
      drawLine("youtube", YT);

      // Hint when there is no series yet
      if (!points.length) {
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.fillText("No live data yet", x0 + 10, y0 + 18);
      }
    }

    draw();

    // Redraw on resize
    const ro = new ResizeObserver(() => draw());
    if (chartWrapRef.current) ro.observe(chartWrapRef.current);

    return () => ro.disconnect();
  }, [viewerSeries]);
const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraMemory, setCameraMemory] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [activationValue, setActivationValue] = useState(500);
  const [donationValue, setDonationValue] = useState(1.0);
  const [tier1, setTier1] = useState(4.19);
  const [tier2, setTier2] = useState(6.99);
  const [tier3, setTier3] = useState(17.49);
  const [prime, setPrime] = useState(2.09);
  const [follow, setFollow] = useState(0.0);
  const [limitTime, setLimitTime] = useState(60);
  const [limitValue, setLimitValue] = useState(200);
  const [chargeLevel, setChargeLevel] = useState(null);
  const [chargeInput, setChargeInput] = useState("");
  const [gracePeriod, setGracePeriod] = useState(60);
  const [drainTime, setDrainTime] = useState(1.66);
  const [dischargeGrace, setDischargeGrace] = useState(2);

  const [contributionsNeeded, setContributionsNeeded] = useState(0.0);

  const chargePercent = activationValue
    ? Math.min(100, (contributionsNeeded / activationValue) * 100).toFixed(2)
    : "0";

  const [isChargingEventDetected, setIsChargingEventDetected] = useState(false);
  const dischargeTimeoutRef = useRef(null);

  const [isChargedHold, setIsChargedHold] = useState(false);
  const chargedTimeoutRef = useRef(null);

  const [tick, setTick] = useState(0);

  async function connectToOBS() {
    try {
      const instance = new OBSWebSocket();

      await instance.connect(`ws://${obsIP}:${obsPort}`, obsPassword || undefined);

      console.log("Connected to OBS");
      setObs(instance);
      setObsConnected(true);

      // 🔍 Fetch scene items to find the one matching your source
      try {
        const { sceneItems } = await instance.call("GetSceneItemList", {
          sceneName: obsSceneName,
        });

        const found = sceneItems.find((item) => item.sourceName === obsSourceName);

        if (!found) {
          console.warn(
            `Could not find source "${obsSourceName}" in scene "${obsSceneName}".`
          );
          setObsSceneItemId(null);
        } else {
          console.log("Found sceneItemId:", found.sceneItemId);
          setObsSceneItemId(found.sceneItemId);
        }
      } catch (e) {
        console.error("Error fetching scene items:", e);
        setObsSceneItemId(null);
      }

      instance.on("ConnectionClosed", () => {
        console.log("OBS disconnected");
        setObsConnected(false);
        setObs(null);
        setObsSceneItemId(null);
      });
    } catch (err) {
      console.error("OBS Connection Error:", err);
      setObsConnected(false);
      setObs(null);
      setObsSceneItemId(null);
    }
  }

  function disconnectOBS() {
    if (obs) {
      try {
        obs.disconnect();
      } catch (err) {
        console.warn("OBS disconnect error:", err);
      }
    }

    setObs(null);
    setObsConnected(false);
    setObsSceneItemId(null);
  }

  useEffect(() => {
    if (!obs || !obsConnected) return;
    if (!obsSceneItemId) {
      console.warn("OBS: No sceneItemId available yet.");
      return;
    }

    async function toggleCamera() {
      try {
        await obs.call("SetSceneItemEnabled", {
          sceneName: obsSceneName,
          sceneItemId: obsSceneItemId,
          sceneItemEnabled: cameraEnabled,
        });

        console.log("Toggled OBS camera:", cameraEnabled);
      } catch (err) {
        console.error("OBS toggle error:", err);
      }
    }

    toggleCamera();
  }, [cameraEnabled, obsConnected, obsSceneItemId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (chargedTimeoutRef.current) {
        clearTimeout(chargedTimeoutRef.current);
      }
    };
  }, []);

  // ⭐ Contribution Calculator
  const getContributionValue = (event) => {
    const category = (event.category || "").toLowerCase();

    if (category === "donation") {
      return Number(event.amount) * Number(donationValue);
    }

    if (category === "bits") {
      return Number(event.amount) * 0.01 * Number(donationValue);
    }

    if (category === "follow") {
      return Number(follow);
    }

    if (category === "mass_gift") {
      const count = Number(event.gift_count || 0);
      const tierCode = event.tier;

      let perSub = 0;
      if (tierCode === "1000") perSub = Number(tier1);
      if (tierCode === "2000") perSub = Number(tier2);
      if (tierCode === "3000") perSub = Number(tier3);

      return count * perSub;
    }

    const raw = event.raw?.message?.[0] || {};
    const plan = raw.subPlan || raw.sub_plan || raw.planName || "";

    if (typeof plan === "string" && plan.toLowerCase().includes("prime")) {
      return Number(prime);
    }

    if (plan === "1000") return Number(tier1);
    if (plan === "2000") return Number(tier2);
    if (plan === "3000") return Number(tier3);

    return 0;
  };

  // ============================================================
  // ⭐ Mass gift display ordering:
  // When a mass_gift arrives AFTER individual gifted_subs, we pull the last N
  // gifted_subs from that gifter and place them under the summary.
  // ============================================================
  function insertMassGiftAboveIndividuals(prev, massEvent) {
    const raw = massEvent.raw?.message?.[0] || {};
    const gifter =
      massEvent.user_name || raw.gifter || raw.gifter_name || raw.displayName || null;
    const count = Number(massEvent.gift_count || raw.amount || 0);

    if (!gifter || !count) {
      return [massEvent, ...prev];
    }

    let moved = 0;
    const taken = [];
    const rest = [];

    for (let i = 0; i < prev.length; i++) {
      const e = prev[i];
      const cat = String(e.category || "").toLowerCase();

      if (moved < count && cat === "gifted_sub") {
        const r = e.raw?.message?.[0] || {};
        const egifter = r.gifter || null;

        if (egifter && egifter === gifter) {
          taken.push(e);
          moved++;
          continue;
        }
      }

      rest.push(e);
    }

    return [massEvent, ...taken, ...rest];
  }

  useEffect(() => {
    function resetDischargeTimer() {
      if (dischargeTimeoutRef.current) {
        clearTimeout(dischargeTimeoutRef.current);
      }

      dischargeTimeoutRef.current = setTimeout(() => {
        setIsChargingEventDetected(false);
      }, dischargeGrace * 60 * 1000);
    }

    const handler = (event) => {
      console.log("Incoming event:", event);

      const category = (event.category || "").toLowerCase();
      const raw = event.raw?.message?.[0] || {};

      // ⭐ MASS GIFT DETECTED — Begin Suppression
      if (category === "mass_gift") {
        setMassGiftTracker({
          gifter: event.user_name,
          remaining: Number(event.gift_count || 0),
        });
      }

      // ⭐ SUPPRESS individual gifted_sub events belonging to mass gift
      if (category === "gifted_sub") {
        const gifter = raw?.gifter;

        if (
          massGiftTracker.gifter &&
          gifter &&
          gifter === massGiftTracker.gifter &&
          massGiftTracker.remaining > 0
        ) {
          setMassGiftTracker((prev) => ({
            gifter: prev.gifter,
            remaining: prev.remaining - 1,
          }));

          return; // suppress this individual gift
        }
      }

      // ⭐ APPLY CONTRIBUTION VALUE
      const value = getContributionValue(event);
      if (featureEnabled && value > 0) {
        setContributionsNeeded((prev) =>
          Math.min(prev + value, Number(activationValue))
        );
      }

      if (!isChargedHold) {
        setIsChargingEventDetected(true);
        resetDischargeTimer();
      }

      const isGiftedSub = category === "gifted_sub";
      const isMassGift = category === "mass_gift";

      const eventWithTime = { ...event, createdAt: Date.now() };

      if (["donation", "bits"].includes(category)) {
        setDonationsAndBits((prev) => [eventWithTime, ...prev]);
        return;
      }

      if (
        ["subscription", "resub", "sub"].includes(category) ||
        isGiftedSub ||
        isMassGift
      ) {
        // ✅ If mass gift summary arrives after the individual gifted subs,
        // reorder so the summary is shown ABOVE them.
        if (isMassGift) {
          setSubscribers((prev) =>
            insertMassGiftAboveIndividuals(prev, eventWithTime)
          );
        } else {
          setSubscribers((prev) => [eventWithTime, ...prev]);
        }
        return;
      }

      if (category === "follow") {
        setFollowers((prev) => [eventWithTime, ...prev]);
        return;
      }

      if (category === "raid") {
        setRaids((prev) => [eventWithTime, ...prev]);
        return;
      }
    };

    socket.on("stream_event", handler);

    return () => {
      socket.off("stream_event", handler);
      if (dischargeTimeoutRef.current) {
        clearTimeout(dischargeTimeoutRef.current);
      }
    };
  }, [
    dischargeGrace,
    isChargedHold,
    featureEnabled,
    activationValue,
    massGiftTracker, // ⭐ required for suppression
  ]);

  useEffect(() => {
    const percent = Number(chargePercent);

    if (!featureEnabled) {
      setIsChargedHold(false);
      if (chargedTimeoutRef.current) {
        clearTimeout(chargedTimeoutRef.current);
        chargedTimeoutRef.current = null;
      }
      return;
    }

    // If fully charged
    if (percent >= 100) {
      // Enter hold immediately if not already in hold
      if (!isChargedHold) {
        setIsChargedHold(true);
      }

      // Always restart timer when gracePeriod changes or percent updates
      if (chargedTimeoutRef.current) {
        clearTimeout(chargedTimeoutRef.current);
      }

      chargedTimeoutRef.current = setTimeout(() => {
        setIsChargedHold(false);
        setIsChargingEventDetected(false);

        // ⭐ Force battery to drop out of 100%
        setContributionsNeeded((prev) => {
          const num = Number(prev);
          return num > 0 ? num - 0.5 : 0;
        });

        chargedTimeoutRef.current = null;
      }, gracePeriod * 60 * 1000);

      return;
    }

    // If percent drops <100 at any time, exit hold
    if (isChargedHold) {
      setIsChargedHold(false);
    }

    if (chargedTimeoutRef.current) {
      clearTimeout(chargedTimeoutRef.current);
      chargedTimeoutRef.current = null;
    }
  }, [chargePercent, featureEnabled, gracePeriod, isChargedHold]);

  useEffect(() => {
    const percent = Number(chargePercent);

    if (!featureEnabled) return;
    if (percent === 0) return;
    if (isChargingEventDetected) return;
    if (isChargedHold) return;

    const drainPerSecond =
      (Number(drainTime) / 100) * (Number(activationValue) / 60);

    const interval = setInterval(() => {
      setContributionsNeeded((prev) => {
        const drain = Number(drainPerSecond);
        if (isNaN(drain) || drain <= 0) return prev;
        const updated = prev - drain;
        return updated > 0 ? updated : 0;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [
    isChargingEventDetected,
    isChargedHold,
    featureEnabled,
    drainTime,
    activationValue,
    chargePercent,
  ]);

  useEffect(() => {
    const charge = Number(chargePercent);

    //
    // STATUS DISABLED → camera forced OFF but remember what it was
    //
    if (!featureEnabled) {
      if (cameraEnabled) {
        setCameraMemory(true); // remember ON state
      }
      setCameraEnabled(false);
      return;
    }

    //
    // STATUS ENABLED
    //

    // CHARGE = 100 → camera ON + remember ON
    if (charge >= 100) {
      if (!cameraEnabled) {
        setCameraEnabled(true);
      }
      setCameraMemory(true); // ALWAYS remember ON
      return;
    }

    // CHARGE = 0 → camera OFF + remember OFF
    if (charge <= 0) {
      setCameraEnabled(false);
      setCameraMemory(false);
      return;
    }

    //
    // CHARGE between 1 and 99:
    // DO NOTHING — keep current camera state
    // (cameraMemory is only used when status toggles)
    //
  }, [featureEnabled, chargePercent]);

  // ⭐ Restore camera state when status is toggled back ON
  useEffect(() => {
    const charge = Number(chargePercent);

    if (!featureEnabled) return; // only care when turning ON

    // If charge forces ON/OFF, don't override
    if (charge >= 100 || charge <= 0) return;

    // Restore memory only when status becomes enabled
    setCameraEnabled(cameraMemory);
  }, [featureEnabled]);

  //
  // HELPERS
  //
  const getRaw = (e) => {
    try {
      if (Array.isArray(e.raw?.message)) return e.raw.message[0];
    } catch {}
    return null;
  };

  const isPrime = (raw) => {
    if (!raw) return false;

    const plan = raw.subPlan || raw.sub_plan || raw.planName;
    if (!plan) return false;

    return plan.toLowerCase().includes("prime");
  };

  const getTier = (raw) => {
    const plan = raw?.subPlan || raw?.sub_plan;
    if (!plan) return null;

    if (plan === "1000") return "Tier 1";
    if (plan === "2000") return "Tier 2";
    if (plan === "3000") return "Tier 3";

    return null;
  };

  const isGifted = (raw) => raw?.gifter && raw.gifter.trim() !== "";
  const getMonths = (raw) => raw?.months || raw?.streak_months || null;
  const getSubType = (raw) => raw?.sub_type || "subscription";

  const getRaidViewers = (e) => {
    const raw = getRaw(e);
    if (raw?.raiders) return raw.raiders;
    return e.amount || null;
  };

  //
  // STREAM STATS
  //
  const stats = useMemo(() => {
    let newSubs = 0;
    let resubs = 0;
    let giftedSubs = 0;
    let primeSubs = 0;
    let tier1Count = 0;
    let tier2Count = 0;
    let tier3Count = 0;

    // ============================
    // ⭐ SUBSCRIBERS (ignore old)
    // ============================
    subscribers.forEach((e) => {
      if (e.isOld) return;

      const category = (e.category || "").toLowerCase();

      if (category === "mass_gift") return;

      const raw = getRaw(e);
      const type = getSubType(raw);
      const tier = getTier(raw);
      const gifted = isGifted(raw);
      const primeFlag = isPrime(raw);
      const months = getMonths(raw);

      if (!gifted) {
        if (type === "subscription" || type === "sub" || (primeFlag && months === 1)) {
          newSubs++;
        }

        if (type === "resub" || (primeFlag && months > 1)) {
          resubs++;
        }
      }

      if (gifted) {
        giftedSubs++;
      }

      if (primeFlag) {
        primeSubs++;
      }

      if (!primeFlag) {
        if (tier === "Tier 1") tier1Count++;
        if (tier === "Tier 2") tier2Count++;
        if (tier === "Tier 3") tier3Count++;
      }
    });

    // ============================
    // ⭐ FOLLOWERS (ignore old)
    // ============================
    const totalFollowers = followers.filter((e) => !e.isOld).length;

    // ============================
    // ⭐ RAIDS (ignore old)
    // ============================
    const totalRaids = raids.filter((e) => !e.isOld).length;

    // ============================
    // ⭐ DONATIONS + BITS (ignore old)
    // ============================
    const liveDonos = donationsAndBits.filter((e) => !e.isOld);

    const totalDonations = liveDonos
      .filter((e) => e.category === "donation")
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

    const totalBits = liveDonos
      .filter((e) => e.category === "bits")
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

    const bitsValue = totalBits * 0.01;
    const totalDonationValue = totalDonations + bitsValue;

    return {
      newSubs,
      resubs,
      giftedSubs,
      primeSubs,
      tier1: tier1Count,
      tier2: tier2Count,
      tier3: tier3Count,
      totalFollowers,
      totalSubs: subscribers.filter(
        (e) => !e.isOld && (e.category || "").toLowerCase() !== "mass_gift"
      ).length,
      totalRaids,
      totalDonations,
      totalBits,
      bitsValue,
      totalDonationValue,
    };
  }, [donationsAndBits, subscribers, followers, raids]);

  //
  // BADGE IMAGES
  //
  const badgeImages = {
    gifted: "/badges/gift.png",
    resub: "/badges/resub.png",
    prime: "/badges/prime.png",
    sub: "/badges/new.png",
    tier1: "/badges/T1.png",
    tier2: "/badges/T2.png",
    tier3: "/badges/T3.png",
  };

  const donationBadgeImages = {
    paypal: "/badges/pp.png",
    stripe: "/badges/card.png",
    bits: "/badges/bits.png",
    youtube: "/badges/yt.png", // ✅ NEW
  };

  //
  // RENDER HELPERS
  //
  const getDonationSource = (e) => {
    // YouTube donations (superchat / supersticker) should use yt badge
    const platform = String(e.platform || "").toLowerCase();
    const source = String(e.source || e.raw?.message?.[0]?.for || "").toLowerCase();
    const isYouTube = platform === "youtube" || source === "youtube_account";

    if (isYouTube) return "youtube";

    const src = String(e.raw?.message?.[0]?.source || "").toLowerCase();
    if (src === "paypal") return "paypal";
    if (src === "stripe") return "stripe";
    return "stripe";
  };

  const renderDonationOrBits = (e, i) => {
    const isBits = e.category === "bits";
    const isSmall = (isBits && e.amount <= 2000) || (!isBits && e.amount <= 20);

    const donoClass = isSmall ? "SL-dono" : "SL-BBdono";

    const donationSource = getDonationSource(e);

    const iconSrc = isBits
      ? donationBadgeImages.bits
      : donationBadgeImages[donationSource] || donationBadgeImages.stripe;

    // For YouTube: backend already set currency; you only want to show currency if not USD.
    // Your UI currently forces $ formatting. We'll leave that alone to avoid breaking existing Twitch/SL.
    // If you want correct symbols later, we can use e.currency + formatting.
    return (
      <div key={i} className="event-item">
        <span className="time-since">{timeSince(e.createdAt)}</span>
        <img
          src={iconSrc}
          alt={donationSource}
          className="badge-icon"
          style={{ width: "22px", marginRight: "5px" }}
        />
        <span className="SL-username">{e.user_name || "Unknown"}</span>
        {" donated "}
        {isBits ? (
          <span className={donoClass}>{e.amount} bits</span>
        ) : (
          <span className={donoClass}>${Number(e.amount).toFixed(2)}</span>
        )}
        {e.message && (
          <>
            {" - "}
            <span className="SL-message">{e.message}</span>
          </>
        )}
      </div>
    );
  };

  const renderSubscriber = (e, i) => {
    const raw = getRaw(e);
    const name = e.user_name || "Unknown";
    const tier = getTier(raw);
    const months = getMonths(raw);
    const type = getSubType(raw);
    const message = (e.message || "").trim();
    const category = (e.category || "").toLowerCase();

    const primeFlag = isPrime(raw);
    const gifted = isGifted(raw);
    const gifterName = raw?.gifter || null;

    const tierClass = primeFlag
      ? "prime"
      : tier
      ? tier.toLowerCase().replace(" ", "")
      : "tier1";

    const tierLabel = primeFlag ? "Prime" : tier;

    const badges = [];

    if (gifted) badges.push({ type: "gifted" });
    if (type === "resub") badges.push({ type: "resub" });
    if (!gifted && type !== "resub") badges.push({ type: "sub" });

    if (!primeFlag) {
      if (tier === "Tier 1") badges.push({ type: "tier1" });
      if (tier === "Tier 2") badges.push({ type: "tier2" });
      if (tier === "Tier 3") badges.push({ type: "tier3" });
    }

    if (primeFlag) badges.push({ type: "prime" });

    if (category === "mass_gift") {
      const raw = getRaw(e);

      const tierCode = e.tier || raw?.sub_plan || raw?.subPlan || "1000";

      let tierName = "Tier 1";
      if (tierCode === "2000") tierName = "Tier 2";
      if (tierCode === "3000") tierName = "Tier 3";

      const count = e.gift_count || raw?.amount || 1;

      return (
        <div key={i} className="event-item">
          <span className="time-since">{timeSince(e.createdAt)}</span>
          <span className="badge-row">
            <img src={badgeImages.gifted} className="badge-icon" />
          </span>{" "}
          <span className="sub-username">{name}</span>{" "}
          gifted <strong className="sub-tenure">{count}</strong>{" "}
          <strong className={tierClass}>{tier || "Tier 1"}</strong> subs to the communes
        </div>
      );
    }

    if (gifted) {
      return (
        <div key={i} className="event-item">
          <span className="time-since">{timeSince(e.createdAt)}</span>

          <span className="badge-row">
            {badges.map((b, idx) => (
              <img
                key={idx}
                src={badgeImages[b.type]}
                alt={b.type}
                className="badge-icon"
              />
            ))}
          </span>{" "}
          <span className="sub-username">{name}</span>{" "}
          has been gifted a <strong className={tierClass}>{tier || "Tier 1"}</strong>{" "}
          sub from{" "}
          <span className="sub-username">{gifterName || "Anonymous"}</span>
        </div>
      );
    }

    const verb = type === "resub" ? "resubbed" : "subbed";
    const duration =
      months && (
        <>
          <span className="sub-tenure">{months}</span> month
          {months > 1 ? "s" : ""}
        </>
      );

    const disallowed = ["prime", "1000", "2000", "3000"];
    const showMessage =
      !gifted && message && !disallowed.includes(message.toLowerCase());

    return (
      <div key={i} className="event-item">
        <span className="time-since">{timeSince(e.createdAt)}</span>
        <span className="badge-row">
          {badges.map((b, idx) => (
            <img
              key={idx}
              src={badgeImages[b.type]}
              alt={b.type}
              className="badge-icon"
            />
          ))}
        </span>{" "}
        <span className="sub-username">{name}</span>{" "}
        <span>
          has <span className={tierClass}>{tierLabel}</span> {verb}
          {duration && <> for {duration}</>}
        </span>
        {showMessage && (
          <>
            {" "}
            - <span className="sub-message">{message}</span>
          </>
        )}
      </div>
    );
  };

  // ✅ Followers: Twitch = "followed", YouTube = "subbed"
  const renderFollower = (e, i) => {
    const platform = String(e.platform || "").toLowerCase();
    const source = String(e.source || "").toLowerCase();
    const msg = String(e.message || "").toLowerCase();

    const isYouTube =
      platform === "youtube" ||
      source === "youtube_account" ||
      msg === "subbed";

    const verb = isYouTube ? "subbed" : "followed";

    return (
      <div key={i} className="event-item">
        <span className="time-since">{timeSince(e.createdAt)}</span>
        <span className="SL-username">{e.user_name || "Unknown"}</span>{" "}
        {verb}
      </div>
    );
  };

  const renderRaid = (e, i) => {
    const viewers = getRaidViewers(e);
    return (
      <div key={i} className="event-item">
        <span className="time-since">{timeSince(e.createdAt)}</span>
        <span className="SL-username">{e.user_name || "Unknown"}</span>
        {viewers && (
          <>
            {" raided with "}
            <span className="SL-tenure">{viewers}</span> raiders
          </>
        )}
        <small>{e.message}</small>
      </div>
    );
  };

  //
  // MAIN UI
  //
  return (
    <div className="dashboard-container">
      <div className="left-panel">
        <div className="panel-box collapsible-panel">
          <div
            className="collapsible-header"
            onClick={() => setSupportStatusOpen(!supportStatusOpen)}
          >
            <h2>Stream Stats</h2>
            <span className="collapse-icon">{supportStatusOpen ? "▲" : "▼"}</span>
          </div>

          {supportStatusOpen && (
            <div className="collapsible-content">
              <p>
                <span className="stats">
                  {twitchGameName ? twitchGameName : "-Offline-"}
                </span>
              </p>

              <p style={{ marginTop: 6, opacity: 0.9 }}>
                Twitch: <span className="stats">{viewerNow.twitch ?? "-"}</span> | YouTube:{" "}
                <span className="stats">{viewerNow.youtube ?? "-"}</span>
              </p>

              <div ref={chartWrapRef} className="viewer-chart-wrap">
                <canvas ref={chartCanvasRef} className="viewer-chart-canvas" />
              </div>
            </div>
          )}
        </div>

        <div className="panel-box collapsible-panel">
          <div
            className="collapsible-header"
            onClick={() => setSupportOpen(!supportOpen)}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ margin: 0 }}>Support Stats</h2>

              <button
                className="toggle-btn"
                style={{ padding: "6px 10px", fontSize: 12 }}
                onClick={(e) => {
                  e.stopPropagation(); // don't collapse panel
                  refreshSupportStats();
                }}
                title="Grey out current events and reset counters"
              >
                Refresh
              </button>
            </div>

            <span className="collapse-icon">{supportOpen ? "▲" : "▼"}</span>
          </div>

          {supportOpen && (
            <div className="collapsible-content">
              <p>
                New Subscribers: <span className="stats">{stats.newSubs}</span>
              </p>
              <p>
                Resubscribers: <span className="stats">{stats.resubs}</span>
              </p>
              <p>
                Gifted Subscribers:{" "}
                <span className="stats">{stats.giftedSubs}</span>
              </p>
              <div className="stat-divider"></div>
              <p>
                Prime Subscribers:{" "}
                <span className="stats">{stats.primeSubs}</span>
              </p>
              <p>
                <span className="tier1-static">Tier 1</span> Subscribers:{" "}
                <span className="stats">{stats.tier1}</span>
              </p>
              <p>
                <span className="tier2-static">Tier 2</span> Subscribers:{" "}
                <span className="stats">{stats.tier2}</span>
              </p>
              <p>
                <span className="tier3-static">Tier 3</span> Subscribers:{" "}
                <span className="stats">{stats.tier3}</span>
              </p>
              <div className="stat-divider"></div>
              <p>
                Total Followers:{" "}
                <span className="stats">{stats.totalFollowers}</span>
              </p>
              <p>
                Total Subscribers:{" "}
                <span className="stats">{stats.totalSubs}</span>
              </p>
              <div className="stat-divider"></div>
              <p>
                Total Streamlabs: ${" "}
                <span className="stats">{stats.totalDonations.toFixed(2)}</span>
              </p>
              <p>
                Total Bits: <span className="stats">{stats.totalBits}</span>
              </p>
              <div className="stat-divider"></div>
              <p>
                Total Donations: ${" "}
                <span className="stats">
                  {stats.totalDonationValue.toFixed(2)}
                </span>
              </p>

              <div className="stat-divider"></div>

              <button
                className={`toggle-btn ${techPanelsEnabled ? "on" : "off"}`}
                onClick={() => setTechPanelsEnabled((v) => !v)}
                style={{ width: "100%", marginTop: 8 }}
              >
                {techPanelsEnabled ? "Disable Cam Control" : "Enable Cam Control"}
              </button>
            </div>
          )}
        </div>

        {techPanelsEnabled && (
          <>
            <div className="panel-box collapsible-panel">
              <div
                className="collapsible-header"
                onClick={() => setCamStatusOpen(!camStatusOpen)}
              >
                <h2>Cam Status</h2>
                <span className="collapse-icon">{camStatusOpen ? "▲" : "▼"}</span>
              </div>

              {camStatusOpen && (
                <div className="collapsible-content">
                  <p>Status: {featureEnabled ? "Enabled" : "Disabled"}</p>
                  <p>Camera: {cameraEnabled ? "Enabled" : "Disabled"}</p>
                  <p>
                    Battery:{" "}
                    {featureEnabled === false
                      ? "Disabled"
                      : Number(chargePercent) === 0
                      ? "Empty"
                      : isChargedHold
                      ? "Charged"
                      : isChargingEventDetected
                      ? "Charging"
                      : Number(chargePercent) >= 100
                      ? "Charged"
                      : "Discharging"}
                  </p>
                  <p>Charge: {Number(chargePercent).toFixed(2)}%</p>
                  <p>
                    Contributions: ${Number(contributionsNeeded).toFixed(2)} / $
                    {Number(activationValue).toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            <div className="panel-box collapsible-panel">
              <div
                className="collapsible-header"
                onClick={() => setCamControlOpen(!camControlOpen)}
              >
                <h2>Cam Control</h2>
                <span className="collapse-icon">{camControlOpen ? "▲" : "▼"}</span>
              </div>

              {camControlOpen && (
                <div className="collapsible-content">
                  <label className="form-row toggle-row">
                    <span>
                      Status:{" "}
                      <strong>{featureEnabled ? "Enabled" : "Disabled"}</strong>
                    </span>
                    <button
                      className={`toggle-btn ${featureEnabled ? "on" : "off"}`}
                      onClick={() => setFeatureEnabled(!featureEnabled)}
                    >
                      {featureEnabled ? "Disable" : "Enable"}
                    </button>
                  </label>

                  <label className="form-row ">
                    <span>Activation: ${Number(activationValue).toFixed(2)}</span>
                    <input
                      type="range"
                      min="50"
                      max="1000"
                      step="5"
                      value={activationValue}
                      onChange={(e) => setActivationValue(e.target.value)}
                    />
                  </label>

                  <h3>Charge Values</h3>

                  <label className="form-row">
                    <span>
                      Donations per $1: ${Number(donationValue).toFixed(2)}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="1.5"
                      step="0.01"
                      value={donationValue}
                      onChange={(e) => setDonationValue(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Tier 1 Sub: ${Number(tier1).toFixed(2)}</span>
                    <input
                      type="range"
                      min="0"
                      max="6"
                      step="0.01"
                      value={tier1}
                      onChange={(e) => setTier1(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Tier 2 Sub: ${Number(tier2).toFixed(2)}</span>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.01"
                      value={tier2}
                      onChange={(e) => setTier2(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Tier 3 Sub: ${Number(tier3).toFixed(2)}</span>
                    <input
                      type="range"
                      min="0"
                      max="25"
                      step="0.01"
                      value={tier3}
                      onChange={(e) => setTier3(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Prime Sub: ${Number(prime).toFixed(2)}</span>
                    <input
                      type="range"
                      min="0"
                      max="6"
                      step="0.01"
                      value={prime}
                      onChange={(e) => setPrime(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Follow: ${Number(follow).toFixed(2)}</span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={follow}
                      onChange={(e) => setFollow(e.target.value)}
                    />
                  </label>

                  <h3>Limit Per-User Contributions</h3>

                  <label className="form-row">
                    <span>Limit: ${limitValue}</span>
                    <input
                      type="range"
                      min="1"
                      max="500"
                      value={limitValue}
                      onChange={(e) => setLimitValue(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Cooldown: {limitTime} minutes</span>
                    <input
                      type="range"
                      min="1"
                      max="720"
                      value={limitTime}
                      onChange={(e) => setLimitTime(e.target.value)}
                    />
                  </label>

                  <h3>Battery Settings</h3>

                  <label className="form-row">
                    <span>Set Charge Level:</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder=" "
                      value={chargeInput}
                      onChange={(e) => setChargeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const val = Number(chargeInput);
                          if (!isNaN(val)) {
                            const newCharge =
                              (val / 100) * Number(activationValue);
                            setContributionsNeeded(newCharge);
                          }
                          setChargeInput(""); // clear box
                        }
                      }}
                    />
                  </label>

                  <label className="form-row">
                    <span>
                      Drain Rate: {Number(drainTime).toFixed(2)}% per min
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={drainTime}
                      onChange={(e) => setDrainTime(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Charged Length: {gracePeriod} mins</span>
                    <input
                      type="range"
                      min="0"
                      max="120"
                      value={gracePeriod}
                      onChange={(e) => setGracePeriod(e.target.value)}
                    />
                  </label>

                  <label className="form-row">
                    <span>Discharge Grace: {dischargeGrace} mins</span>
                    <input
                      type="range"
                      min="0"
                      max="60"
                      value={dischargeGrace}
                      onChange={(e) => setDischargeGrace(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="panel-box collapsible-panel">
              <div
                className="collapsible-header"
                onClick={() => setObsStatus(!obsStatus)}
              >
                <h2>OBS Connection</h2>
                <span className="collapse-icon">{obsStatus ? "▲" : "▼"}</span>
              </div>

              {obsStatus && (
                <div className="collapsible-content">
                  <div className="form-row">
                    <span>OBS IP:</span>
                    <input
                      type="text"
                      value={obsIP}
                      onChange={(e) => setObsIP(e.target.value)}
                    />
                  </div>

                  <div className="form-row">
                    <span>Port:</span>
                    <input
                      type="text"
                      value={obsPort}
                      onChange={(e) => setObsPort(e.target.value)}
                    />
                  </div>

                  <div className="form-row">
                    <span>Password:</span>
                    <input
                      type="password"
                      value={obsPassword}
                      onChange={(e) => setObsPassword(e.target.value)}
                    />
                  </div>

                  <div className="form-row">
                    <button
                      className="toggle-btn"
                      onClick={() =>
                        obsConnected ? disconnectOBS() : connectToOBS()
                      }
                    >
                      {obsConnected ? "Disconnect OBS" : "Connect to OBS"}
                    </button>

                    <span
                      style={{
                        marginLeft: "10px",
                        color: obsConnected ? "#3ccf4e" : "#e05a5a",
                        fontWeight: "bold",
                      }}
                    >
                      ● {obsConnected ? "Connected" : "Disconnected"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="middle-panel">
        <div className="panel-box large-panel">
          <h2>Subscribers</h2>

          {/* ROW WRAPPER */}
          {subscribers.map((e, i) => (
            <div
              key={i}
              className={`row row-animate ${e.isOld ? "old-event" : ""} ${
                i % 2 === 0 ? "row-even" : "row-odd"
              }`}
            >
              {renderSubscriber(e, i)}
            </div>
          ))}
        </div>
      </div>

      <div className="right-panel">
        <div className="panel-box top-right">
          <h2>Donations</h2>

          {donationsAndBits.map((e, i) => (
            <div
              key={i}
              className={`row row-animate ${e.isOld ? "old-event" : ""} ${
                i % 2 === 0 ? "row-even" : "row-odd"
              }`}
            >
              {renderDonationOrBits(e, i)}
            </div>
          ))}
        </div>

        <div className="bottom-right">
          <div className="bottom-half panel-box small-box">
            <h2>Followers</h2>

            {followers.map((e, i) => (
              <div
                key={i}
                className={`row row-animate ${e.isOld ? "old-event" : ""} ${
                  i % 2 === 0 ? "row-even" : "row-odd"
                }`}
              >
                {renderFollower(e, i)}
              </div>
            ))}
          </div>

          <div className="bottom-half panel-box small-box">
            <h2>Raids</h2>

            {raids.map((e, i) => (
              <div
                key={i}
                className={`row row-animate ${e.isOld ? "old-event" : ""} ${
                  i % 2 === 0 ? "row-even" : "row-odd"
                }`}
              >
                {renderRaid(e, i)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
